import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import {
  Users, FolderOpen, Search, FileText, Calendar, Settings,
  BookOpen, CreditCard, ClipboardList, AlertCircle, Inbox, Plus,
  CloudOff
} from 'lucide-react';
import {
  NoLessonsIllustration,
  NoStudentsIllustration,
  NoSearchResultsIllustration,
  OfflineIllustration,
  ErrorIllustration,
} from './EmptyStateIllustrations';

/**
 * Enterprise Empty State Component
 * Displays actionable empty states with helpful messaging and CTA
 */

const ICONS = {
  users: Users,
  students: Users,
  teachers: Users,
  clients: Users,
  folder: FolderOpen,
  groups: FolderOpen,
  search: Search,
  file: FileText,
  documents: FileText,
  calendar: Calendar,
  lessons: Calendar,
  settings: Settings,
  book: BookOpen,
  subjects: BookOpen,
  payment: CreditCard,
  billing: CreditCard,
  checklist: ClipboardList,
  attendance: ClipboardList,
  error: AlertCircle,
  offline: CloudOff,
  inbox: Inbox,
  default: Inbox
};

export default function EmptyState({
  icon = 'default',
  title,
  description,
  action = null,
  secondaryAction = null,
  size = 'medium',
  variant = 'default',
  // Phase 5.5 — optional SVG/illustration slot. When provided, replaces the
  // icon ring. Use this for hero illustrations on top-level empty states.
  illustration = null,
  children
}) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('emptyStates.default.title', { defaultValue: 'No data found' });
  const resolvedDescription =
    description ?? t('emptyStates.default.description', { defaultValue: 'There are no items to display.' });

  const IconComponent = typeof icon === 'string' ? (ICONS[icon] || ICONS.default) : icon;

  const sizes = {
    small: {
      padding: '1.5rem',
      iconSize: 36,
      iconBgSize: '60px',
      titleSize: '1rem',
      descSize: '0.875rem',
      gap: '0.75rem'
    },
    medium: {
      padding: '2.5rem',
      iconSize: 40,
      iconBgSize: '80px',
      titleSize: '1.125rem',
      descSize: '0.9375rem',
      gap: '1rem'
    },
    large: {
      padding: '3.5rem',
      iconSize: 48,
      iconBgSize: '96px',
      titleSize: '1.25rem',
      descSize: '1rem',
      gap: '1.25rem'
    }
  };

  const sizeConfig = sizes[size] || sizes.medium;

  // Phase 5.5 / 6.1 — token-driven variants so dark mode adapts cleanly.
  const variants = {
    default: {
      iconBg: 'var(--bg-tertiary)',
      iconColor: 'var(--text-tertiary)',
      borderColor: 'var(--border-light)',
    },
    search: {
      iconBg: 'var(--primary-50)',
      iconColor: 'var(--primary-dark)',
      borderColor: 'var(--primary-100)',
    },
    error: {
      iconBg: 'var(--danger-50)',
      iconColor: 'var(--danger-600)',
      borderColor: 'var(--danger-100)',
    },
    offline: {
      iconBg: 'var(--warning-50)',
      iconColor: 'var(--warning-600)',
      borderColor: 'var(--warning-100)',
    }
  };

  const variantConfig = variants[variant] || variants.default;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: sizeConfig.padding,
        minHeight: size === 'large' ? '400px' : size === 'small' ? '180px' : '280px',
      }}
    >
      {/* Hero illustration (Phase 5.5) — pages can pass any SVG/JSX.
          Falls back to the dashed icon ring below. */}
      {illustration ? (
        <div style={{ marginBottom: sizeConfig.gap }}>
          {illustration}
        </div>
      ) : (
        <div
          style={{
            width: sizeConfig.iconBgSize,
            height: sizeConfig.iconBgSize,
            borderRadius: '50%',
            backgroundColor: variantConfig.iconBg,
            border: `2px dashed ${variantConfig.borderColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: sizeConfig.gap,
          }}
        >
          <IconComponent
            size={sizeConfig.iconSize}
            color={variantConfig.iconColor}
            strokeWidth={1.5}
          />
        </div>
      )}

      {/* Title */}
      <h3
        style={{
          margin: 0,
          marginBottom: '0.375rem',
          fontSize: sizeConfig.titleSize,
          fontWeight: '600',
          color: 'var(--color-text-primary, #1A1F36)',
          letterSpacing: '-0.01em',
        }}
      >
        {resolvedTitle}
      </h3>

      {/* Description */}
      <p
        style={{
          margin: 0,
          marginBottom: action ? sizeConfig.gap : 0,
          fontSize: sizeConfig.descSize,
          color: 'var(--color-text-tertiary, #697386)',
          maxWidth: '360px',
          lineHeight: '1.6'
        }}
      >
        {resolvedDescription}
      </p>

      {/* Custom content */}
      {children}

      {/* Action Buttons */}
      {(action || secondaryAction) && (
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            marginTop: sizeConfig.gap,
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}
        >
          {action && (
            <button
              onClick={action.onClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                backgroundColor: 'var(--primary-dark)',
                color: 'var(--text-inverse)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 2px rgba(53, 87, 96, 0.15)',
                minHeight: 44,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--primary-700)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--primary-dark)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {action.icon ? <action.icon size={16} /> : <Plus size={16} />}
              {action.label}
            </button>
          )}

          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                backgroundColor: 'transparent',
                color: 'var(--color-text-secondary, #3C4257)',
                border: '1.5px solid var(--color-border, #E2E6EE)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-surface-alt, #F1F4F8)';
                e.currentTarget.style.borderColor = 'var(--color-border-dark, #CDD3DE)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'var(--color-border, #E2E6EE)';
              }}
            >
              {secondaryAction.icon && <secondaryAction.icon size={16} />}
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Pre-configured empty states for common scenarios.
 *
 * Each preset resolves its own copy through i18n. They used to carry hardcoded
 * English, so an Arabic user reading the lessons page was told "No lessons
 * scheduled" in the middle of an otherwise-Arabic screen.
 */
function PresetEmptyState({ slug, titleDefault, descriptionDefault, ...rest }) {
  // Center-aware copy: a sport center reads clients/coaches/sessions here.
  const t = useSmartTranslation();
  return (
    <EmptyState
      title={t(`emptyStates.${slug}.title`, { defaultValue: titleDefault })}
      description={t(`emptyStates.${slug}.description`, { defaultValue: descriptionDefault })}
      {...rest}
    />
  );
}

export const EmptyStates = {
  NoStudents: (props) => (
    <PresetEmptyState
      icon="students"
      illustration={<NoStudentsIllustration />}
      slug="noStudents"
      titleDefault={"No students yet"}
      descriptionDefault={"Add your first student to get started with your educational center."}
      {...props}
    />
  ),

  NoClients: (props) => (
    <PresetEmptyState
      icon="clients"
      illustration={<NoStudentsIllustration />}
      slug="noClients"
      titleDefault={"No clients yet"}
      descriptionDefault={"Add your first client to get started with your therapy center."}
      {...props}
    />
  ),

  NoTeachers: (props) => (
    <PresetEmptyState
      icon="teachers"
      slug="noTeachers"
      titleDefault={"No teachers yet"}
      descriptionDefault={"Add teachers to your center to start organizing groups and lessons."}
      {...props}
    />
  ),

  NoGroups: (props) => (
    <PresetEmptyState
      icon="groups"
      slug="noGroups"
      titleDefault={"No groups yet"}
      descriptionDefault={"Create your first group to organize students and teachers."}
      {...props}
    />
  ),

  NoSearchResults: (props) => (
    <PresetEmptyState
      icon="search"
      variant="search"
      illustration={<NoSearchResultsIllustration />}
      slug="noSearchResults"
      titleDefault={"No results found"}
      descriptionDefault={"We couldn't find anything matching your search. Try different keywords or filters."}
      {...props}
    />
  ),

  NoLessons: (props) => (
    <PresetEmptyState
      icon="calendar"
      illustration={<NoLessonsIllustration />}
      slug="noLessons"
      titleDefault={"No lessons scheduled"}
      descriptionDefault={"Schedule lessons for your groups to track attendance and progress."}
      {...props}
    />
  ),

  NoBilling: (props) => (
    <PresetEmptyState
      icon="billing"
      slug="noBilling"
      titleDefault={"No billing entries"}
      descriptionDefault={"Create billing entries to track payments and outstanding balances."}
      {...props}
    />
  ),

  Error: (props) => (
    <PresetEmptyState
      icon="error"
      variant="error"
      illustration={<ErrorIllustration />}
      slug="error"
      titleDefault={"Something went wrong"}
      descriptionDefault={"We couldn't load the data. Please try again."}
      {...props}
    />
  ),

  Offline: (props) => (
    <PresetEmptyState
      icon="offline"
      variant="offline"
      illustration={<OfflineIllustration />}
      slug="offline"
      titleDefault={"You're offline"}
      descriptionDefault={"This page needs an internet connection to load. We'll sync any changes you make when you're back online."}
      {...props}
    />
  ),
};
